import { useState, useEffect, useCallback } from 'react';
import type { SidebarView } from '../types';
import type { ChatFiltersState } from '../components/ChatFilterDefinitions';

/**
 * Chaves do localStorage para preferências do Chat
 */
const STORAGE_KEYS = {
  LEFT_SIDEBAR_EXPANDED: 'chat_left_sidebar_expanded',
  RIGHT_SIDEBAR_OPEN: 'chat_right_sidebar_open',
  CURRENT_VIEW: 'chat_current_view',
  SHOW_ONLY_MY_ATTENDANCES: 'chat_show_only_my_attendances',
  CHAT_FILTERS: 'chat_filters',
} as const;

/**
 * Interface de preferências do Chat
 */
export interface ChatPreferences {
  leftSidebarExpanded: boolean;
  rightSidebarOpen: boolean;
  currentView: SidebarView;
  showOnlyMyAttendances: boolean;
  chatFilters: ChatFiltersState;
}

/**
 * Hook customizado para gerenciar preferências do Chat com persistência em localStorage
 *
 * Salva e recupera automaticamente:
 * - Estado da sidebar esquerda (expandida/recolhida)
 * - Estado da sidebar direita (aberta/fechada)
 * - View selecionada (ai, chats, groups, contacts, history)
 * - Filtro "Apenas meus atendimentos" (apenas na view Chats)
 *
 * @example
 * ```tsx
 * const {
 *   leftSidebarExpanded,
 *   setLeftSidebarExpanded,
 *   rightSidebarOpen,
 *   setRightSidebarOpen,
 *   currentView,
 *   setCurrentView,
 *   showOnlyMyAttendances,
 *   setShowOnlyMyAttendances
 * } = useChatPreferences();
 * ```
 */
export const useChatPreferences = () => {
  // Carregar preferências do localStorage na inicialização
  const [leftSidebarExpanded, setLeftSidebarExpandedState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.LEFT_SIDEBAR_EXPANDED);
      // Default: sidebar expandida (true)
      return stored ? JSON.parse(stored) : true;
    } catch {
      return true;
    }
  });

  const [rightSidebarOpen, setRightSidebarOpenState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.RIGHT_SIDEBAR_OPEN);
      // Default: sidebar fechada (false)
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  const [currentView, setCurrentViewState] = useState<SidebarView>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CURRENT_VIEW);
      // Default: chats
      return (stored as SidebarView) || 'chats';
    } catch {
      return 'chats';
    }
  });

  const [showOnlyMyAttendances, setShowOnlyMyAttendancesState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SHOW_ONLY_MY_ATTENDANCES);
      // Default: false (mostrar todas as conversas)
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  const [chatFilters, setChatFiltersState] = useState<ChatFiltersState>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CHAT_FILTERS);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Remover search ao restaurar - é transiente, não deve persistir
        if (parsed && typeof parsed === 'object') {
          delete parsed.search;
          return parsed;
        }
      }
      return {};
    } catch {
      return {};
    }
  });

  // Salvar leftSidebarExpanded no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.LEFT_SIDEBAR_EXPANDED, JSON.stringify(leftSidebarExpanded));
    } catch (error) {
      console.error('Erro ao salvar leftSidebarExpanded no localStorage:', error);
    }
  }, [leftSidebarExpanded]);

  // Salvar rightSidebarOpen no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.RIGHT_SIDEBAR_OPEN, JSON.stringify(rightSidebarOpen));
    } catch (error) {
      console.error('Erro ao salvar rightSidebarOpen no localStorage:', error);
    }
  }, [rightSidebarOpen]);

  // NOTA: currentView é persistido de forma SÍNCRONA no setter (setCurrentView abaixo),
  // não via useEffect. Trocar de aba pode navegar (navigate) e DESMONTAR o sidebar antes
  // de um efeito pós-render rodar — se a gravação dependesse de useEffect, o sidebar
  // remontado leria um valor obsoleto e a aba errada apareceria selecionada.

  // Salvar showOnlyMyAttendances no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.SHOW_ONLY_MY_ATTENDANCES, JSON.stringify(showOnlyMyAttendances));
    } catch (error) {
      console.error('Erro ao salvar showOnlyMyAttendances no localStorage:', error);
    }
  }, [showOnlyMyAttendances]);

  // Salvar chatFilters no localStorage sempre que mudar (sem search)
  useEffect(() => {
    try {
      // Salvar apenas filtros não-transientes (excluir search)
      const filtersToSave = { ...chatFilters };
      delete filtersToSave.search;
      // Só salvar se há filtros reais
      const hasFilters = Object.keys(filtersToSave).length > 0;
      if (hasFilters) {
        localStorage.setItem(STORAGE_KEYS.CHAT_FILTERS, JSON.stringify(filtersToSave));
      } else {
        localStorage.removeItem(STORAGE_KEYS.CHAT_FILTERS);
      }
    } catch (error) {
      console.error('Erro ao salvar chatFilters no localStorage:', error);
    }
  }, [chatFilters]);

  // Setter de currentView que persiste SÍNCRONO no localStorage (ver NOTA acima).
  // Grava antes de qualquer navigate/desmonte para o sidebar remontado ler o valor correto.
  const setCurrentView = useCallback((view: SidebarView) => {
    setCurrentViewState(view);
    try {
      localStorage.setItem(STORAGE_KEYS.CURRENT_VIEW, view);
    } catch (error) {
      console.error('Erro ao salvar currentView no localStorage:', error);
    }
  }, []);

  // Wrapper para setChatFilters que preserva a referência
  const setChatFilters = useCallback((filters: ChatFiltersState) => {
    setChatFiltersState(filters);
  }, []);

  return {
    // Estado da sidebar esquerda
    leftSidebarExpanded,
    setLeftSidebarExpanded: setLeftSidebarExpandedState,

    // Estado da sidebar direita
    rightSidebarOpen,
    setRightSidebarOpen: setRightSidebarOpenState,

    // View selecionada
    currentView,
    setCurrentView,

    // Filtro "Apenas meus atendimentos"
    showOnlyMyAttendances,
    setShowOnlyMyAttendances: setShowOnlyMyAttendancesState,

    // Filtros avançados do chat (persistidos no localStorage)
    chatFilters,
    setChatFilters,
  };
};
